/**
 * Formulario de Edición de Empleado
 * Fecha: 16 Oct 2025
 * 
 * Permite editar solo información personal y laboral del empleado
 */
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeesService } from '@/features/rh/api/employees.service';
import { contractsService } from '@/features/rh/api/contracts.service';
import type { Employee, UpdateEmployeeDto, EstadoCivil, TipoContrato, Jornada } from '@/types/employee.types';
import type { Area } from '@/features/configuracion/catalogos/types/catalogs.types';
import { LoadingSpinner, Modal } from '@/shared/components';
import { Notification, useNotification } from '@/shared/components/Notification';
import {
  User,
  Briefcase,
  Phone,
  DollarSign,
  ArrowLeft,
  Save,
  X,
  AlertCircle,
  FileText,
  Upload,
  Download,
  ExternalLink,
  Calendar,
} from 'lucide-react';
import { usePermissions, EMPLOYEE_PERMISSIONS } from '@/shared/hooks/usePermissions';
import SelectBuscable from '@/shared/components/SelectBuscable';
import { Badge } from '@/shared/components/Badge';
import { comoEventoDeCambio } from '@/shared/lib/opcionesDeSelect';
import { filterOutMasterUsers } from '@/shared/lib/userFilters';
import { config } from '@/config/env'

const ESTADOS_CIVILES: { value: EstadoCivil; label: string }[] = [
  { value: 'soltero', label: 'Soltero/a' },
  { value: 'casado', label: 'Casado/a' },
  { value: 'union_libre', label: 'Unión Libre' },
  { value: 'divorciado', label: 'Divorciado/a' },
  { value: 'viudo', label: 'Viudo/a' },
];

const TIPOS_CONTRATO: { value: TipoContrato; label: string }[] = [
  { value: 'tiempo_completo', label: 'Tiempo completo' },
  { value: 'medio_tiempo', label: 'Medio tiempo' },
  { value: 'por_proyecto', label: 'Por proyecto' },
  { value: 'por_horas', label: 'Por horas' },
];

const JORNADAS: { value: Jornada; label: string }[] = [
  { value: 'tiempo_completo', label: 'Tiempo Completo' },
  { value: 'medio_tiempo', label: 'Medio Tiempo' },
  { value: 'por_horas', label: 'Por Horas' },
  { value: 'reducida', label: 'Reducida' },
  { value: 'mixta', label: 'Mixta' },
  { value: 'nocturna', label: 'Nocturna' },
];

interface FormErrors {
  [key: string]: string;
}

export const EmployeeEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { notification, showSuccess, showError, hideNotification } = useNotification();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  // Estados para catálogos
  const [areas, setAreas] = useState<Area[]>([]);
  const [puestos, setPuestos] = useState<any[]>([]);
  const [puestosFiltrados, setPuestosFiltrados] = useState<any[]>([]);
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);

  const [formData, setFormData] = useState<UpdateEmployeeDto>({});

  // Estados para contratos
  const [contracts, setContracts] = useState<any[]>([]);
  const [contractsTotal, setContractsTotal] = useState(0);
  const [isLoadingContracts, setIsLoadingContracts] = useState(false);

  // Estados para carga de contratos
  const [showUploadContractModal, setShowUploadContractModal] = useState(false);
  const [uploadContractFile, setUploadContractFile] = useState<File | null>(null);
  const [uploadContractName, setUploadContractName] = useState('');
  const [isUploadingContract, setIsUploadingContract] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canUpdate = hasPermission(EMPLOYEE_PERMISSIONS.EMPLOYEES_UPDATE);

  useEffect(() => {
    if (id) {
      loadEmployee();
      loadCatalogs();
      loadContracts();
    }
  }, [id]);

  const loadEmployee = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await employeesService.getById(Number(id));
      setEmployee(data);

      // Inicializar formulario con datos del empleado
      setFormData({
        // Datos personales
        curp: data.curp,
        rfc: data.rfc,
        nss: data.nss || '',
        fechaNacimiento: data.fechaNacimiento,
        telefonoPersonal: data.telefonoPersonal || '',
        estadoCivil: data.estadoCivil,
        direccion: data.direccion || '',

        // Datos laborales
        areaId: data.areaId,
        puestoId: data.puestoId,
        fechaIngreso: data.fechaIngreso,
        tipoContrato: data.tipoContrato,
        jornada: data.jornada,
        sueldoBrutoMensual: data.sueldoBrutoMensual,
        jefeInmediatoId: data.jefeInmediatoId || '',
        prestaciones: data.prestaciones || '',

        // Contacto emergencia
        emergenciaContactoNombre: data.emergenciaContactoNombre || '',
        emergenciaContactoRelacion: data.emergenciaContactoRelacion || '',
        emergenciaContactoTelefono: data.emergenciaContactoTelefono || '',
      });

      // Filtrar puestos por área inicial
      if (data.areaId) {
        filterPuestosByArea(data.areaId);
      }
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar empleado:', err);
      setError(err.message || 'Error al cargar empleado');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCatalogs = async () => {
    setLoadingCatalogs(true);
    try {
      const [areasRes, puestosRes, usuariosRes] = await Promise.all([
        fetch(`${config.apiBaseUrl}/catalogos/areas`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${config.apiBaseUrl}/catalogos/puestos`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${config.apiBaseUrl}/users`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      const areasData = await areasRes.json();
      const puestosData = await puestosRes.json();
      const usuariosData = await usuariosRes.json();

      setAreas(Array.isArray(areasData) ? areasData.filter((a: Area) => a.activa) : []);
      setPuestos(Array.isArray(puestosData) ? puestosData.filter((p: any) => p.activo) : []);
      setUsuarios(
        filterOutMasterUsers(
          Array.isArray(usuariosData) ? usuariosData.filter((u: any) => u.isActive) : [],
        ),
      );
    } catch (err) {
      console.error('Error al cargar catálogos:', err);
    } finally {
      setLoadingCatalogs(false);
    }
  };

  const loadContracts = async () => {
    try {
      setIsLoadingContracts(true);
      const response = await contractsService.getByEmployee(Number(id));
      setContracts(response.data);
      setContractsTotal(response.total);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar contratos:', err);
    } finally {
      setIsLoadingContracts(false);
    }
  };

  const filterPuestosByArea = (areaId: number) => {
    if (areaId) {
      const filtered = puestos.filter((p) => p.areaId === areaId && p.activo);
      setPuestosFiltrados(filtered);
    } else {
      setPuestosFiltrados([]);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Si cambia el área, filtrar puestos y limpiar selección de puesto
    if (name === 'areaId') {
      const areaId = Number(value);
      filterPuestosByArea(areaId);

      setFormData((prev) => ({
        ...prev,
        areaId: areaId,
        puestoId: 0, // Resetear puesto
      }));

      // Limpiar error de puesto
      if (errors.puestoId) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.puestoId;
          return newErrors;
        });
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Limpiar error del campo
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validaciones de campos requeridos
    if (!formData.curp?.trim()) {
      newErrors.curp = 'CURP es requerido';
    }
    if (!formData.rfc?.trim()) {
      newErrors.rfc = 'RFC es requerido';
    }
    if (!formData.fechaNacimiento) {
      newErrors.fechaNacimiento = 'Fecha de nacimiento es requerida';
    }
    if (!formData.areaId) {
      newErrors.areaId = 'Departamento/Área es requerida';
    }
    if (!formData.puestoId) {
      newErrors.puestoId = 'Puesto es requerido';
    }
    if (!formData.fechaIngreso) {
      newErrors.fechaIngreso = 'Fecha de ingreso es requerida';
    }
    if (!formData.sueldoBrutoMensual || formData.sueldoBrutoMensual <= 0) {
      newErrors.sueldoBrutoMensual = 'Sueldo debe ser mayor a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Preparar datos para enviar
      const updateData: UpdateEmployeeDto = {
        // Datos personales
        curp: formData.curp,
        rfc: formData.rfc,
        nss: formData.nss || undefined,
        fechaNacimiento: formData.fechaNacimiento,
        telefonoPersonal: formData.telefonoPersonal || undefined,
        estadoCivil: formData.estadoCivil,
        direccion: formData.direccion || undefined,

        // Datos laborales
        areaId: formData.areaId,
        puestoId: formData.puestoId,
        fechaIngreso: formData.fechaIngreso,
        tipoContrato: formData.tipoContrato,
        jornada: formData.jornada,
        sueldoBrutoMensual: formData.sueldoBrutoMensual,
        jefeInmediatoId: formData.jefeInmediatoId || undefined,
        prestaciones: formData.prestaciones || undefined,

        // Contacto emergencia
        emergenciaContactoNombre: formData.emergenciaContactoNombre || undefined,
        emergenciaContactoRelacion: formData.emergenciaContactoRelacion || undefined,
        emergenciaContactoTelefono: formData.emergenciaContactoTelefono || undefined,
      };

      await employeesService.update(Number(id), updateData);

      // Redirigir a vista de detalle
      navigate(`/employees/${id}`);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al actualizar empleado:', err);
      setError(err.message || 'Error al actualizar empleado');
    } finally {
      setIsSaving(false);
    }
  };

  // Funciones para carga de contratos
  const handleUploadContractClick = () => {
    setShowUploadContractModal(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const validateAndSetFile = (file: File) => {
    const validTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!validTypes.includes(file.type)) {
      showError('Archivo inválido', 'Por favor selecciona un archivo de Word (.doc o .docx)');
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showError('Archivo muy grande', 'El archivo no debe superar los 10MB');
      return;
    }

    setUploadContractFile(file);
  };

  const handleUploadContract = async () => {
    if (!employee || !uploadContractFile || !uploadContractName.trim()) {
      showError('Datos incompletos', 'Por favor completa todos los campos');
      return;
    }

    try {
      setIsUploadingContract(true);
      await contractsService.uploadContract(
        employee.id,
        uploadContractFile,
        {
          category: '',
          state: 'borrador',
          version: '1.0',
          expirationDate: ''
        }
      );

      // Recargar contratos
      await loadContracts();

      // Cerrar modal y limpiar
      setShowUploadContractModal(false);
      setUploadContractFile(null);
      setUploadContractName('');

      showSuccess('Contrato subido', 'El contrato se ha subido exitosamente');
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al subir contrato:', err);
      showError('Error al subir contrato', err.message || 'No se pudo subir el contrato');
    } finally {
      setIsUploadingContract(false);
    }
  };

  const handleCancelUploadContract = () => {
    setShowUploadContractModal(false);
    setUploadContractFile(null);
    setUploadContractName('');
  };

  if (!canUpdate) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="w-full px-2 sm:px-3 lg:px-4">
          <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 rounded-r-lg p-6 shadow-sm">
            <h3 className="text-lg font-bold text-orange-700 dark:text-orange-400 mb-2">
              Sin permisos
            </h3>
            <p className="text-orange-600 dark:text-orange-500 font-semibold leading-relaxed">
              No tienes permisos para editar empleados.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Cargando datos del empleado..." />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="w-full px-2 sm:px-3 lg:px-4">
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-r-lg p-6 shadow-sm">
            <h3 className="text-lg font-bold text-red-700 dark:text-red-400 mb-2">
              Error
            </h3>
            <p className="text-red-600 dark:text-red-500 font-semibold">
              {error || 'No se pudo cargar el empleado'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 my-6">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/employees')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>

          <div className="flex items-center justify-between">
            <div>
              <TituloDePantalla nivel="interna" className="mb-2">
                Editar Empleado
              </TituloDePantalla>
              <p className="text-gray-600 dark:text-gray-400">
                {employee.user?.firstName} {employee.user?.lastName} - {employee.curp}
              </p>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-r-lg p-4 shadow-sm">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 dark:text-red-400 font-semibold">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Personal */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-6">
              <User className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Información Personal
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* CURP */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CURP <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="curp"
                  value={formData.curp || ''}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 h-10 border rounded-md ${errors.curp
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                  placeholder="18 caracteres"
                  maxLength={18}
                />
                {errors.curp && (
                  <p className="text-red-500 text-xs mt-1">{errors.curp}</p>
                )}
              </div>

              {/* RFC */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  RFC <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="rfc"
                  value={formData.rfc || ''}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 h-10 border rounded-md ${errors.rfc
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                  placeholder="13 caracteres"
                  maxLength={13}
                />
                {errors.rfc && (
                  <p className="text-red-500 text-xs mt-1">{errors.rfc}</p>
                )}
              </div>

              {/* NSS */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  NSS (Número de Seguridad Social)
                </label>
                <input
                  type="text"
                  name="nss"
                  value={formData.nss || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 h-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="11 dígitos"
                  maxLength={11}
                />
              </div>

              {/* Fecha de Nacimiento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fecha de Nacimiento <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="fechaNacimiento"
                  value={formData.fechaNacimiento ? formData.fechaNacimiento.split('T')[0] : ''}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 h-10 border rounded-md ${errors.fechaNacimiento
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                />
                {errors.fechaNacimiento && (
                  <p className="text-red-500 text-xs mt-1">{errors.fechaNacimiento}</p>
                )}
              </div>

              {/* Teléfono Personal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Teléfono Personal
                </label>
                <input
                  type="tel"
                  name="telefonoPersonal"
                  value={formData.telefonoPersonal || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 h-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="10 dígitos"
                  maxLength={10}
                />
              </div>

              {/* Estado Civil */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Estado Civil <span className="text-red-500">*</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  name="estadoCivil"
                  opciones={ESTADOS_CIVILES}
                  value={formData.estadoCivil || 'soltero'}
                  onChange={(valor) => handleInputChange(comoEventoDeCambio('estadoCivil', valor))}
                  canDelete={false}
                  placeholderDeBusqueda="Buscar estado civil..."
                />
              </div>

              {/* Dirección */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Dirección
                </label>
                <textarea
                  name="direccion"
                  value={formData.direccion || ''}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Calle, número, colonia, ciudad, estado, CP"
                />
              </div>
            </div>
          </div>

          {/* Información Laboral */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-6">
              <Briefcase className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Información Laboral
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Área */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Departamento/Área <span className="text-red-500">*</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  name="areaId"
                  opciones={areas.map((area) => ({ value: String(area.id), label: area.nombre }))}
                  value={formData.areaId ? String(formData.areaId) : ''}
                  onChange={(valor) => handleInputChange(comoEventoDeCambio('areaId', valor))}
                  placeholder="Seleccione un Departamento/Área"
                  disabled={loadingCatalogs}
                  error={Boolean(errors.areaId)}
                  canDelete={false}
                  placeholderDeBusqueda="Buscar área..."
                />
                {errors.areaId && (
                  <p className="text-red-500 text-xs mt-1">{errors.areaId}</p>
                )}
              </div>

              {/* Puesto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Puesto <span className="text-red-500">*</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  name="puestoId"
                  opciones={puestosFiltrados.map((puesto) => ({ value: String(puesto.id), label: puesto.nombre }))}
                  value={formData.puestoId ? String(formData.puestoId) : ''}
                  onChange={(valor) => handleInputChange(comoEventoDeCambio('puestoId', valor))}
                  placeholder={
                    !formData.areaId
                      ? 'Primero seleccione un Departamento/Área'
                      : loadingCatalogs
                        ? 'Cargando puestos...'
                        : 'Seleccione un puesto'
                  }
                  disabled={loadingCatalogs || !formData.areaId}
                  error={Boolean(errors.puestoId)}
                  canDelete={false}
                  placeholderDeBusqueda="Buscar puesto..."
                />
                {errors.puestoId && (
                  <p className="text-red-500 text-xs mt-1">{errors.puestoId}</p>
                )}
                {!loadingCatalogs && formData.areaId && puestosFiltrados.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    ✓ {puestosFiltrados.length} puesto(s) disponible(s)
                  </p>
                )}
              </div>

              {/* Fecha de Ingreso */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fecha de Ingreso <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="fechaIngreso"
                  value={formData.fechaIngreso ? formData.fechaIngreso.split('T')[0] : ''}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 h-10 border rounded-md ${errors.fechaIngreso
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                />
                {errors.fechaIngreso && (
                  <p className="text-red-500 text-xs mt-1">{errors.fechaIngreso}</p>
                )}
              </div>

              {/* Tipo de Contrato */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo de Contrato <span className="text-red-500">*</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  name="tipoContrato"
                  opciones={TIPOS_CONTRATO}
                  value={formData.tipoContrato || 'indeterminado'}
                  onChange={(valor) => handleInputChange(comoEventoDeCambio('tipoContrato', valor))}
                  canDelete={false}
                  placeholderDeBusqueda="Buscar tipo de contrato..."
                />
              </div>

              {/* Jornada */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Jornada <span className="text-red-500">*</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  name="jornada"
                  opciones={JORNADAS}
                  value={formData.jornada || 'tiempo_completo'}
                  onChange={(valor) => handleInputChange(comoEventoDeCambio('jornada', valor))}
                  canDelete={false}
                  placeholderDeBusqueda="Buscar jornada..."
                />
              </div>

              {/* Sueldo Bruto Mensual */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sueldo Bruto Mensual <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="sueldoBrutoMensual"
                    value={formData.sueldoBrutoMensual || ''}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className={`w-full pl-10 pr-3 py-2 border rounded-md ${errors.sueldoBrutoMensual
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2`}
                    placeholder="0.00"
                  />
                </div>
                {errors.sueldoBrutoMensual && (
                  <p className="text-red-500 text-xs mt-1">{errors.sueldoBrutoMensual}</p>
                )}
              </div>

              {/* Jefe Inmediato */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Jefe Inmediato
                </label>
                <SelectBuscable
                  variante="formulario"
                  name="jefeInmediatoId"
                  opciones={[{ value: '', label: 'Sin jefe inmediato' }, ...usuarios.map((usuario) => ({ value: String(usuario.id), label: `${usuario.firstName} ${usuario.lastName} (${usuario.email})` }))]}
                  value={formData.jefeInmediatoId || ''}
                  onChange={(valor) => handleInputChange(comoEventoDeCambio('jefeInmediatoId', valor))}
                  disabled={loadingCatalogs}
                  canDelete={false}
                  placeholderDeBusqueda="Buscar usuario..."
                />
              </div>

              {/* Prestaciones */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Prestaciones
                </label>
                <textarea
                  name="prestaciones"
                  value={formData.prestaciones || ''}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Aguinaldo, vacaciones, seguro de vida, vales de despensa, etc."
                />
              </div>
            </div>
          </div>

          {/* Contacto de Emergencia */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-6">
              <Phone className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Contacto de Emergencia
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  name="emergenciaContactoNombre"
                  value={formData.emergenciaContactoNombre || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 h-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre del contacto"
                />
              </div>

              {/* Relación */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Relación
                </label>
                <input
                  type="text"
                  name="emergenciaContactoRelacion"
                  value={formData.emergenciaContactoRelacion || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 h-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Esposo/a, Padre/Madre, Hermano/a"
                />
              </div>

              {/* Teléfono */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  name="emergenciaContactoTelefono"
                  value={formData.emergenciaContactoTelefono || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 h-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="10 dígitos"
                  maxLength={10}
                />
              </div>
            </div>
          </div>

          {/* Contratos Subidos */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Contratos Subidos
                </h2>
                {contractsTotal > 0 && (
                  <Badge color="morado" className="ml-2">
                    {contractsTotal}
                  </Badge>
                )}
              </div>
              <button
                type="button"
                onClick={handleUploadContractClick}
                className={estiloDeBoton('primario')}
              >
                <Upload className="w-4 h-4" />
                Subir Contrato
              </button>
            </div>

            {isLoadingContracts ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="md" text="Cargando contratos..." />
              </div>
            ) : contracts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {contracts.map((contract) => (
                  <div
                    key={contract.id}
                    className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate mb-1">
                          {contract.nombre}
                        </h3>
                        {contract.tipoArchivo && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {contract.tipoArchivo.includes('word') ? 'Word Document' : 'Documento'}
                          </p>
                        )}
                      </div>
                      <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0 ml-2" />
                    </div>

                    <div className="space-y-2 mb-3">
                      {contract.tamanoArchivo && (
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Tamaño:</span>
                          <span>{(contract.tamanoArchivo / 1024).toFixed(2)} KB</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                        <Calendar className="w-3 h-3" />
                        <span>Subido: {new Date(contract.createdAt).toLocaleDateString('es-MX', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}</span>
                      </div>
                    </div>

                    {contract.url && (
                      <div className="flex gap-2">
                        <a
                          href={contract.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md flex items-center justify-center gap-2 transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Ver
                        </a>
                        <a
                          href={contract.url}
                          download
                          className="flex-1 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded-md flex items-center justify-center gap-2 transition-colors"
                        >
                          <Download className="w-3 h-3" />
                          Descargar
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  No hay contratos subidos
                </p>
                <button
                  type="button"
                  onClick={handleUploadContractClick}
                  className={estiloDeBoton('primario')}
                >
                  <Upload className="w-4 h-4" />
                  Subir Primer Contrato
                </button>
              </div>
            )}
          </div>

          {/* Botones de Acción */}
          <div className="flex items-center justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate(`/employees/${id}`)}
              className={estiloDeBoton('secundario')}
            >
              <X className="w-4 h-4" />
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={estiloDeBoton('primario')}
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>

        {/* Modal de Carga de Contrato */}
        <Modal
          isOpen={showUploadContractModal}
          onClose={handleCancelUploadContract}
          title="Subir Contrato"
          size="lg"
        >
          <div className="space-y-4">
            {/* Nombre del Contrato */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nombre del Contrato *
              </label>
              <input
                type="text"
                value={uploadContractName}
                onChange={(e) => setUploadContractName(e.target.value)}
                placeholder="Ej: Contrato Temporal 2025"
                className="w-full px-3 py-2 h-10 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Área de Carga de Archivo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Archivo del Contrato *
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50'
                  }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {uploadContractFile ? (
                  <div className="space-y-2">
                    <FileText className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {uploadContractFile.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(uploadContractFile.size / 1024).toFixed(2)} KB
                    </p>
                    <button
                      type="button"
                      onClick={() => setUploadContractFile(null)}
                      className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                    >
                      Eliminar archivo
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Arrastra y suelta el archivo aquí o
                    </p>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
                    >
                      Selecciona un archivo
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Solo archivos Word (.doc, .docx) - Máximo 10MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Botones de Acción */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleCancelUploadContract}
                className={estiloDeBoton('secundario')}
                disabled={isUploadingContract}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleUploadContract}
                disabled={!uploadContractFile || !uploadContractName.trim() || isUploadingContract}
                className={estiloDeBoton('primario')}
              >
                {isUploadingContract ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Subir Contrato
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>

        {/* Notification */}
        {notification.isVisible && (
          <Notification
            type={notification.type}
            title={notification.title}
            message={notification.message}
            isVisible={notification.isVisible}
            onClose={hideNotification}
          />
        )}
      </div>
    </div>
  );
};
